import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ROUTES } from "@/constants/routes";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { useAppNavigate } from "@/hooks/pane-routing";
import { SearchResultItem, useGlobalSearch } from "@/hooks/useGlobalSearch";
import * as analytics from "@/utils/analytics";
import { route } from "@/utils/route";
import { InboxItem } from "@cohesive/api-client";
import { Spinner } from "@cohesive/ui-components";
import { useDebounce } from "@uidotdev/usehooks";
import { Inbox, Plug, ScrollText, Search, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { GlobalSearchResultItem } from "./GlobalSearchResultItem";

interface GlobalSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  openSource?: "sidebar" | "keyboard_shortcut";
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({
  open,
  onOpenChange,
  openSource,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [resultSelectedInSession, setResultSelectedInSession] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const { searchResults, isLoading } = useGlobalSearch(debouncedSearchQuery);
  const appNavigate = useAppNavigate();
  const openSettings = useOpenSettings();
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      // Track search opened
      if (openSource) {
        analytics.trackGlobalSearchOpened({ source: openSource });
      }
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      setResultSelectedInSession(false);
    } else {
      setSearchQuery("");
    }
  }, [open, openSource]);

  // Track search query submission
  useEffect(() => {
    if (debouncedSearchQuery && !isLoading && searchResults) {
      const categories = (
        Object.keys(searchResults) as Array<keyof typeof searchResults>
      ).filter(
        (key) =>
          key !== "totalCount" &&
          Array.isArray(searchResults[key]) &&
          searchResults[key].length > 0,
      );

      analytics.trackGlobalSearchQuerySubmitted({
        query: debouncedSearchQuery,
        resultCount: searchResults.totalCount || 0,
        categories: categories as string[],
      });
    }
  }, [debouncedSearchQuery, isLoading, searchResults]);

  const handleSelect = (item: SearchResultItem, position: number) => {
    // Track result selection
    analytics.trackGlobalSearchResultSelected({
      itemId: item.id,
      itemName: item.name,
      itemType: item.itemType,
      query: debouncedSearchQuery,
      position,
    });

    // Mark that a result was selected in this session
    setResultSelectedInSession(true);

    // Clear search and close dialog
    setSearchQuery("");
    onOpenChange(false);

    switch (item.itemType) {
      case "skills":
        appNavigate({
          pane: "rightPane",
          to: route(ROUTES.SKILLS.SHOW, { id: item.id }),
        });
        break;
      case "connections":
        openSettings(
          route(ROUTES.SETTINGS.CONNECTORS_SETTINGS, {
            integrationId: item.id,
          }),
        );
        break;
      case "inbox": {
        const inboxItem = item.data as InboxItem;
        if (inboxItem.flow_session?.id) {
          appNavigate({
            pane: "rightPane",
            to: route(ROUTES.SESSIONS.SESSION, {
              sessionId: inboxItem.flow_session.id,
            }),
          });
        }
        break;
      }
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "skills":
        return ScrollText;
      case "connections":
        return Plug;
      case "inbox":
        return Inbox;
      default:
        return Search;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "skills":
        return "Skills";
      case "connections":
        return "Connectors";
      case "inbox":
        return "Chats";
      default:
        return category;
    }
  };

  const hasResults = searchResults && searchResults.totalCount > 0;
  const hasQuery = searchQuery.trim().length > 0;
  const isSearching =
    isLoading || (hasQuery && searchQuery !== debouncedSearchQuery);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Track search closed
      analytics.trackGlobalSearchClosed({
        hadQuery: hasQuery,
        hadResults: hasResults || false,
        selectedResult: resultSelectedInSession,
      });
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[700px] p-0 gap-0" hideCloseButton>
        <div className="px-1 py-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              ref={inputRef}
              type="text"
              placeholder="Search across inbox, skills, connectors and more..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  handleOpenChange(false);
                }
              }}
              className="w-full h-10 pl-9 pr-20 text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="p-1 hover:bg-muted rounded-sm transition-colors"
                  type="button"
                >
                  <X className="h-3 w-3 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="h-[400px] overflow-y-auto">
          {!hasQuery ? (
            <div className="pt-6 pb-12 text-center text-sm text-muted-foreground h-full flex items-center justify-center">
              Start typing to search...
            </div>
          ) : isSearching ? (
            <div className="pt-6 pb-12 h-full flex items-center justify-center">
              <Spinner size="md" />
            </div>
          ) : !hasResults ? (
            <div className="pt-6 pb-12 text-center text-sm text-muted-foreground h-full flex items-center justify-center">
              No results found for "{searchQuery}"
            </div>
          ) : (
            <div className="py-2">
              {(() => {
                let globalPosition = 0;
                return Object.entries(searchResults)
                  .filter(
                    ([key, items]) =>
                      key !== "totalCount" &&
                      Array.isArray(items) &&
                      items.length > 0,
                  )
                  .map(([category, items]) => {
                    const Icon = getCategoryIcon(category);
                    const label = getCategoryLabel(category);
                    return (
                      <div key={category} className="mb-4 last:mb-0">
                        <div className="flex items-center gap-2 px-4 py-2 text-xs font-medium text-muted-foreground">
                          <Icon className="h-3.5 w-3.5" />
                          <span>{label}</span>
                          <span>({(items as SearchResultItem[]).length})</span>
                        </div>
                        <div className="space-y-1 px-2">
                          {(items as SearchResultItem[]).map((item) => {
                            const currentPosition = globalPosition++;
                            return (
                              <div
                                key={item.id}
                                className="rounded-sm hover:bg-accent transition-colors cursor-pointer"
                                onClick={() =>
                                  handleSelect(item, currentPosition)
                                }
                              >
                                <GlobalSearchResultItem
                                  item={item}
                                  onSelect={() =>
                                    handleSelect(item, currentPosition)
                                  }
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
              })()}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
